import React, { useState } from "react";
import styled, { useTheme, keyframes } from "styled-components";
import { isMobile } from "react-device-detect";
import getExternalLinkProps from "../../util/getExternalLinkProps";
import Grid from "../../components/Box/Grid";
import Box from "../../components/Box/Box";
import getThemeValue from "../../util/getThemeValue";
import Text from "../../components/Text/Text";
import Heading from "../../components/Heading/Heading";
import { Button } from "../../components/Button";
import { ModalBody, ModalCloseButton, ModalContainer, ModalHeader, ModalTitle } from "../Modal";
import WalletCard, { MoreWalletCard } from "./WalletCard";
import config, { walletLocalStorageKey } from "./config";
import { Config, Login } from "./types";

interface Props {
  login: Login;
  onDismiss?: () => void;
  displayCount?: number;
  t: (key: string) => string;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const WalletWrapper = styled(Box)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: ${({ theme }) => `linear-gradient(135deg, ${theme.colors.backgroundAlt} 0%, ${theme.colors.backgroundAlt2} 100%)`};
  border-radius: 24px;
  margin: 8px;
  padding: 24px;
  backdrop-filter: blur(10px);
  animation: ${fadeIn} 0.3s ease-out;
`;

const StyledModalContainer = styled(ModalContainer)`
  max-width: 420px;
  width: 100%;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  background: ${({ theme }) => theme.colors.backgroundAlt};
`;

const MinimalHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 16px 0 16px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
`;

const StyledModalBody = styled(ModalBody)`
  padding: 0 24px 24px 24px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
`;

const WalletList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

/**
 * Checks local storage if we have saved the last wallet the user connected with
 * If we find something we put it at the top of the list
 *
 * @returns sorted config
 */
const getPreferredConfig = (walletConfig: Config[]) => {
  const sortedConfig = walletConfig.sort((a: Config, b: Config) => a.priority - b.priority);

  const preferredWalletName = localStorage?.getItem(walletLocalStorageKey);

  if (!preferredWalletName) {
    return sortedConfig;
  }

  const preferredWallet = sortedConfig.find((sortedWalletConfig) => sortedWalletConfig.title === preferredWalletName);

  if (!preferredWallet) {
    return sortedConfig;
  }

  return [
    preferredWallet,
    ...sortedConfig
      .filter((sortedWalletConfig) => sortedWalletConfig.title !== preferredWalletName)
      .filter((sortedWalletConfig) =>
        typeof sortedWalletConfig.mobileOnly === "boolean" ? sortedWalletConfig.mobileOnly && isMobile : true
      ),
  ];
};

const ConnectModal: React.FC<Props> = ({ login, onDismiss = () => null, displayCount = 6, t }) => {
  const theme = useTheme();
  const sortedConfig = getPreferredConfig(config);
  // Show all wallets, no more filtering or "More" button needed
  const walletsToShow = window.ethereum?.isTrust
    ? sortedConfig.filter((wallet) => wallet.title !== "WalletConnect")
    : sortedConfig;

  return (
    <StyledModalContainer minWidth="400px">
      <MinimalHeader>
        <ModalCloseButton onDismiss={onDismiss} />
      </MinimalHeader>
      <StyledModalBody width={["380px", null, "400px"]}>
        <WalletList>
          {walletsToShow.map((wallet) => (
            <WalletCard key={wallet.title} walletConfig={wallet} login={login} onDismiss={onDismiss} />
          ))}
        </WalletList>
      </StyledModalBody>
    </StyledModalContainer>
  );
};

export default ConnectModal;
